import { businessSchema, type Business } from '@/features/business/model/business.ts'
import type { TenantConfig } from '@/tenant/model/tenantConfig.ts'
import { demoTenantConfig } from '@/tenant/demo/tenantConfigs.ts'

/**
 * DEVELOPMENT FALLBACK — not an integration and not a backend.
 *
 * Built from `tenants/<slug>/business.json`, the same file the publishing
 * pipeline consumes. Nothing about a studio is written in TypeScript, so a
 * business name cannot become a hardcoded constant in `src`.
 *
 * Used only when no backend is configured. See `src/lib/data-source.ts`.
 */
export function businessFromConfig(config: TenantConfig): Business {
  const workingDays = [0, 1, 2, 3, 4, 5, 6].map((weekday) => {
    const day = config.workingHours.find((item) => item.weekday === weekday)
    return day === undefined
      ? { weekday }
      : { weekday, opensAt: day.opensAt, closesAt: day.closesAt }
  })

  return businessSchema.parse({
    name: config.name,
    accent: [config.accent.light, config.accent.dark],
    tagline: config.tagline,
    about: config.about,
    address: config.address,
    addressNote: config.addressNote,
    phone: config.phone,
    phoneDisplay: config.phoneDisplay,
    workingDays,
    timezone: config.timezone,
  })
}

export function demoBusinessFor(slug: string): Business | null {
  const config = demoTenantConfig(slug)
  return config === null ? null : businessFromConfig(config)
}

/** The default studio, for call sites that have no slug in hand. */
export const demoBusiness: Business = (() => {
  const business = demoBusinessFor('')
  if (business === null) {
    throw new Error('No tenant configs found in tenants/. Run: npm run tenant:new -- <slug>')
  }
  return business
})()
